import type { z } from "zod/v4";
import {
  LIBRARY_MANAGEMENT_STORED_OPERATIONS,
  LIBRARY_MANAGEMENT_WIRE_OPERATIONS,
} from "./library-management/schema";
import {
  BRAND_IMAGE_GUARDRAILS_STORED_OPERATIONS,
  BRAND_IMAGE_GUARDRAILS_WIRE_OPERATIONS,
} from "./brand-image-guardrails/schema";
import {
  BRAND_PROFILE_STORED_OPERATIONS,
  BRAND_PROFILE_WIRE_OPERATIONS,
} from "./brand-profile/schema";
import {
  COLLECTION_IMAGE_GUARDRAILS_STORED_OPERATIONS,
  COLLECTION_IMAGE_GUARDRAILS_WIRE_OPERATIONS,
} from "./collection-image-guardrails/schema";
import {
  ENTITY_STORED_OPERATIONS,
  ENTITY_WIRE_OPERATIONS,
} from "./context-entity/schema";
import {
  DEFINITION_STORED_OPERATIONS,
  DEFINITION_WIRE_OPERATIONS,
} from "./definition/schema";
import { ASSET_STORED_OPERATIONS, ASSET_WIRE_OPERATIONS } from "./asset/schema";
import type { ReviewTargetKind } from "./view";
import {
  RESIZE_GUIDANCE_STORED_OPERATIONS,
  RESIZE_GUIDANCE_WIRE_OPERATIONS,
} from "./resize-guidance/schema";

// Each target's operation schemas, keyed in REVIEW_TARGETS order. packages/app loads this
// through llm/schema: import only schema modules here.
const OPERATIONS_BY_KIND = {
  library_management: {
    wire: LIBRARY_MANAGEMENT_WIRE_OPERATIONS,
    stored: LIBRARY_MANAGEMENT_STORED_OPERATIONS,
  },
  entity: { wire: ENTITY_WIRE_OPERATIONS, stored: ENTITY_STORED_OPERATIONS },
  brand_profile: {
    wire: BRAND_PROFILE_WIRE_OPERATIONS,
    stored: BRAND_PROFILE_STORED_OPERATIONS,
  },
  brand_image_guardrails: {
    wire: BRAND_IMAGE_GUARDRAILS_WIRE_OPERATIONS,
    stored: BRAND_IMAGE_GUARDRAILS_STORED_OPERATIONS,
  },
  collection_image_guardrails: {
    wire: COLLECTION_IMAGE_GUARDRAILS_WIRE_OPERATIONS,
    stored: COLLECTION_IMAGE_GUARDRAILS_STORED_OPERATIONS,
  },
  definition: {
    wire: DEFINITION_WIRE_OPERATIONS,
    stored: DEFINITION_STORED_OPERATIONS,
  },
  asset: { wire: ASSET_WIRE_OPERATIONS, stored: ASSET_STORED_OPERATIONS },
  resize_guidance: {
    wire: RESIZE_GUIDANCE_WIRE_OPERATIONS,
    stored: RESIZE_GUIDANCE_STORED_OPERATIONS,
  },
} satisfies Record<
  ReviewTargetKind,
  { wire: readonly z.ZodType[]; stored: readonly z.ZodType[] }
>;

// Each kind's stored operations, as its stored schemas parse them. A concrete object type, so
// writing to one kind's entry through a generic key cannot accept another kind's operations.
type OperationByKind = {
  [K in ReviewTargetKind]: z.output<
    (typeof OPERATIONS_BY_KIND)[K]["stored"][number]
  >;
};
type OpTypeByKind = {
  [K in ReviewTargetKind]: OperationByKind[K]["op_type"];
};

// A stored operation of one kind. With no kind, any kind's.
export type ReviewTargetOperation<
  K extends ReviewTargetKind = ReviewTargetKind,
> = OperationByKind[K];

export type ReviewTargetOpType<K extends ReviewTargetKind = ReviewTargetKind> =
  OpTypeByKind[K];

// Spread as tuples in key order, so z.infer keeps every member.
export const REVIEW_TARGET_WIRE_OPERATIONS = [
  ...OPERATIONS_BY_KIND.library_management.wire,
  ...OPERATIONS_BY_KIND.entity.wire,
  ...OPERATIONS_BY_KIND.brand_profile.wire,
  ...OPERATIONS_BY_KIND.brand_image_guardrails.wire,
  ...OPERATIONS_BY_KIND.collection_image_guardrails.wire,
  ...OPERATIONS_BY_KIND.definition.wire,
  ...OPERATIONS_BY_KIND.asset.wire,
  ...OPERATIONS_BY_KIND.resize_guidance.wire,
] as const;

export function reviewTargetWireOperations(
  kind: ReviewTargetKind,
): readonly (typeof REVIEW_TARGET_WIRE_OPERATIONS)[number][] {
  return OPERATIONS_BY_KIND[kind].wire;
}

export const REVIEW_TARGET_STORED_OPERATIONS = [
  ...OPERATIONS_BY_KIND.library_management.stored,
  ...OPERATIONS_BY_KIND.entity.stored,
  ...OPERATIONS_BY_KIND.brand_profile.stored,
  ...OPERATIONS_BY_KIND.brand_image_guardrails.stored,
  ...OPERATIONS_BY_KIND.collection_image_guardrails.stored,
  ...OPERATIONS_BY_KIND.definition.stored,
  ...OPERATIONS_BY_KIND.asset.stored,
  ...OPERATIONS_BY_KIND.resize_guidance.stored,
] as const;
